/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    CardHeader,
    Field,
    Input,
    Spinner,
    makeStyles,
    Subtitle1,
    Body2,
    Link,
    Label,
    mergeClasses,
} from '@fluentui/react-components';
import { EyeOffRegular, EyeRegular } from '@fluentui/react-icons';
import { Column, Row } from '../../common';
import { BrandCard } from '../../common/BrandCard';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { useToasts } from '../../toasts/useToasts';
import { TermsAndPoliciesDialog } from './TermsAndPoliciesDialog';
import { AuthInfo } from '../../../data/types';
import { useLogin } from '../../../auth/useLogin';
import Logo, { CasePortalLogo } from '../../common/appLogo';
import { useValidation } from '../../../data/types/validation';

const useStyles = makeStyles({
    field: {
        width: '100%',
        maxWidth: '16rem',
    },
    loginBtn: {
        width: 'auto',
    },
    errorTxt: {
        color: 'red',
        width: '70%',
        textAlign: 'center',
    },
    logo: {
        // fontSize: '2rem',
        margin:'none'
    },
    portalLogo: {
        fontSize: '1.75rem',
    },
});
enum LoginState {
    login = 'Login',
    requestPWReset = 'Forgot Password?',
    resetPassword = 'Reset Password',
}
type LoginProps = {
    error?: string;
};

export const Login = ({ error }: LoginProps) => {
    //Hooks
    const { login, requestPWReset, resetPW } = useLogin();
    const toasts = useToasts();
    const navTo = useNavigate();
    const { pathname, state } = useLocation();
    const loginState = state?.loginState || LoginState.login;
    const validate = useValidation();

    //State
    const [waiting, setWaiting] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [dismissedError, setDismissedError] = useState<string>();
    const [showResetPasswords, setShowResetPasswords] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const loginBtnClasses = mergeClasses(commonStyles.button.root, styles.loginBtn);

    //Forms
    const loginForm = useFormik({
        initialValues: {
            username: '',
            password: '',
        } as AuthInfo,
        onSubmit: async (values, { resetForm }) => {
            if (!!values.username && !!values.password) {
                setWaiting(true);
                setHasError(false);

                try {
                    await login(values);
                } catch (er) {
                    setHasError(true);
                    setWaiting(false);
                }
            }
            resetForm();
        },
        validationSchema: validate.validateLogin.schema,
    });

    const requestPWResetForm = useFormik({
        initialValues: {
            username: '',
        },
        onSubmit: async (values, { resetForm }) => {
            if (!!values.username) {
                setHasError(false);
                try {
                    await requestPWReset(values.username);
                    setUsernameInResetForm(values.username);

                    changeLoginState(LoginState.resetPassword);
                } catch {
                    setHasError(true);
                }
            }
            resetForm();
        },
        validationSchema: validate.validateRequestPWReset.schema,
    });

    const resetPWForm = useFormik({
        initialValues: {
            username: '',
            resetCode: '',
            newPassword: '',
            confirmPassword: '',
        },
        onSubmit: async (values, { resetForm }) => {
            if (!!values.username && !!values.resetCode && !!values.newPassword && !!values.confirmPassword) {
                setHasError(false);
                try {
                    await resetPW(values);
                    toasts.pw.notifyPWresetSuccessful();
                } catch {
                    //setHasError(true);
                    toasts.pw.notifyPWresetError();
                }
                changeLoginState(LoginState.login);
                resetForm();
            }
        },
        validationSchema: validate.validateResetPW.schema,
    });

    //Logic
    const changeLoginState = (newLoginState: LoginState, data?: string) => {
        navTo(pathname, { state: { loginState: newLoginState, data } });
    };
    const setUsernameInResetForm = (username: string) => {
        resetPWForm.resetForm({ values: { ...resetPWForm.values, username } });
    };

    //UI Events
    const onCredentialsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHasError(false);
        setDismissedError(error);
        loginForm.handleChange(event);
    };
    const onKeyDownPW = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            loginForm.submitForm();
        }
    };
    const clickRequestPWReset = (e: any) => {
        e.stopPropagation();

        setHasError(false);
        changeLoginState(LoginState.requestPWReset);
    };
    const onClickLogin = (e: any) => {
        e.stopPropagation();
        loginForm.submitForm();
    };
    const onClickRequestPWReset = (e: any) => {
        e.stopPropagation();
        requestPWResetForm.submitForm();
    };
    const onClickResetPW = (e: any) => {
        e.stopPropagation();
        resetPWForm.submitForm();
    };
    const onClickToggleShowResetPasswords = (e: any) => {
        e.stopPropagation();
        setShowResetPasswords((prev) => !prev);
    };

    const showResetPasswordsToggle = (testId: string) => (
        <Button
            appearance='transparent'
            size='small'
            tabIndex={-1}
            icon={showResetPasswords ? <EyeOffRegular /> : <EyeRegular />}
            aria-label={showResetPasswords ? 'Hide password' : 'Show password'}
            title={showResetPasswords ? 'Hide password' : 'Show password'}
            onClick={onClickToggleShowResetPasswords}
            data-testid={testId}
        />
    );

    return (
        <Column align='center' vAlign='middle'>
            <BrandCard minHeight='400px' minWidth='320px' maxWidth='480px' borderRadius='15px' align='center' padding='px-3 py-3'>
                <CardHeader
                    header={
                        <Column align='center'>
                            {/* <Logo className={styles.logo} /> */}
                            <CasePortalLogo className={styles.portalLogo} />
                        </Column>
                    }
                />
                <Column flex='1' width='100%' align='center' padding='pt-3 px-3'>
                    <Subtitle1>{loginState}</Subtitle1>
                    {loginState == LoginState.login && (
                        <>
                            {!!error && error !== dismissedError && (
                                <Body2 data-testid='portal-permission-error-text' className={styles.errorTxt}>
                                    {error}
                                </Body2>
                            )}
                            {!!hasError && (
                                <Body2 data-testid='login-error-text' className={styles.errorTxt}>
                                    The username or password you entered is incorrect.
                                </Body2>
                            )}
                            {!!waiting ? (
                                <Column padding='pt-4'>
                                    <Spinner
                                        size='large'
                                        label='Authenticating'
                                        data-testid='login-authenticating-spinner'
                                    />
                                </Column>
                            ) : (
                                <Column width='100%' align='center' padding='px-2'>
                                    <Field
                                        label='Username'
                                        className={styles.field}
                                        required
                                        validationMessage={loginForm.errors.username}
                                    >
                                        <Input
                                            name='username'
                                            tabIndex={1}
                                            value={loginForm?.values.username}
                                            onChange={onCredentialsChange}
                                            data-testid='login-username'
                                            maxLength={validate.validateLogin.username.maxLength}
                                        />
                                    </Field>
                                    <Field
                                        label='Password'
                                        className={styles.field}
                                        required
                                        validationMessage={loginForm.errors.password}
                                    >
                                        <Input
                                            name='password'
                                            type='password'
                                            tabIndex={2}
                                            value={loginForm?.values.password}
                                            onChange={onCredentialsChange}
                                            onKeyDown={onKeyDownPW}
                                            data-testid='login-password'
                                        />
                                    </Field>
                                    <Button
                                        className={loginBtnClasses}
                                        appearance='primary'
                                        onClick={onClickLogin}
                                        disabled={!loginForm.dirty || !loginForm.isValid}
                                        data-testid='login-submit'
                                    >
                                        Login
                                    </Button>
                                    <Link
                                        className={commonStyles.button.root}
                                        tabIndex={-1}
                                        onClick={clickRequestPWReset}
                                        data-testid='login-forgot-password-link'
                                    >
                                        Forgot Password?
                                    </Link>
                                </Column>
                            )}
                        </>
                    )}
                    {loginState == LoginState.requestPWReset && (
                        <>
                            {!!hasError && (
                                <Body2 data-testid='login-reset-request-error' className={styles.errorTxt}>
                                    There was an error sending you the reset code. Please try again later.
                                </Body2>
                            )}
                            <Column width='100%' align='center' padding='px-2'>
                                <Field
                                    label='Username'
                                    className={styles.field}
                                    required
                                    validationMessage={requestPWResetForm.errors.username}
                                >
                                    <Input
                                        name='username'
                                        tabIndex={1}
                                        value={requestPWResetForm?.values.username}
                                        onChange={requestPWResetForm?.handleChange}
                                        data-testid='login-reset-request-username'
                                        maxLength={validate.validateLogin.username.maxLength}
                                    />
                                </Field>
                                <Button
                                    className={loginBtnClasses}
                                    appearance='primary'
                                    onClick={onClickRequestPWReset}
                                    data-testid='login-reset-request-submit'
                                >
                                    Send Reset Code
                                </Button>
                            </Column>
                        </>
                    )}
                    {loginState == LoginState.resetPassword && (
                        <>
                            {!!hasError && (
                                <Body2 data-testid='login-reset-error' className={styles.errorTxt}>
                                    There was an error resetting your password. Please try again later.
                                </Body2>
                            )}
                            <Column width='100%' align='center' padding='px-2'>
                                <Field label='Username' className={styles.field}>
                                    <Label tabIndex={-1}>{resetPWForm?.values.username}</Label>
                                </Field>
                                <Field
                                    label='Code'
                                    className={styles.field}
                                    required
                                    validationState={resetPWForm.errors.resetCode ? 'error' : 'none'}
                                    validationMessage={resetPWForm.errors.resetCode}
                                >
                                    <Input
                                        name='resetCode'
                                        tabIndex={1}
                                        value={resetPWForm?.values.resetCode}
                                        onChange={resetPWForm?.handleChange}
                                        data-testid='login-reset-code'
                                    />
                                </Field>
                                <Field
                                    label='New Password'
                                    className={styles.field}
                                    required
                                    validationState={resetPWForm.errors.newPassword ? 'error' : 'none'}
                                    validationMessage={resetPWForm.errors.newPassword}
                                >
                                    <Input
                                        type={showResetPasswords ? 'text' : 'password'}
                                        name='newPassword'
                                        tabIndex={1}
                                        value={resetPWForm?.values.newPassword}
                                        onChange={resetPWForm?.handleChange}
                                        data-testid='login-reset-new-password'
                                        maxLength={validate.validateResetPW.newPassword.maxLength}
                                        contentAfter={showResetPasswordsToggle('login-reset-new-password-toggle')}
                                    />
                                </Field>
                                <Field
                                    label='Confirm Password'
                                    className={styles.field}
                                    required
                                    validationState={resetPWForm.errors.confirmPassword ? 'error' : 'none'}
                                    validationMessage={resetPWForm.errors.confirmPassword}
                                >
                                    <Input
                                        type={showResetPasswords ? 'text' : 'password'}
                                        name='confirmPassword'
                                        tabIndex={1}
                                        value={resetPWForm?.values.confirmPassword}
                                        onChange={resetPWForm?.handleChange}
                                        data-testid='login-reset-confirm-password'
                                        maxLength={validate.validateResetPW.newPassword.maxLength}
                                        contentAfter={showResetPasswordsToggle('login-reset-confirm-password-toggle')}
                                    />
                                </Field>
                                <Button
                                    className={loginBtnClasses}
                                    appearance='primary'
                                    onClick={onClickResetPW}
                                    data-testid='login-reset-submit'
                                >
                                    Reset Password
                                </Button>
                            </Column>
                        </>
                    )}
                </Column>
                <Column width='100%' noGap vAlign='bottom' align='right' height='auto' padding='pr-2 pt-2'>
                    <Row>
                        <Column>
                            <Logo className={styles.logo} />
                        </Column>
                        <Column align='right'>
                            <TermsAndPoliciesDialog>
                                <Link
                                    className={commonStyles.button.root}
                                    tabIndex={-1}
                                    data-testid='login-terms-of-service-link'
                                >
                                    Terms of Service
                                </Link>
                            </TermsAndPoliciesDialog>
                        </Column>
                    </Row>
                </Column>
            </BrandCard>
        </Column>
    );
};
